import { effectiveKeySkills } from "@/lib/cvBuilderPresentation";
import { useState } from "react";
import { useCvBuilder } from "@/stores/cvBuilderStore";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Plus, X } from "lucide-react";

const SkillsStep = () => {
    const {
        draft,
        setSkillsField,
        addSkillCategory,
        updateSkillCategory,
        removeSkillCategory
    } = useCvBuilder();
    const { skills } = draft;
    const automaticSkills = effectiveKeySkills(skills);
    const [newHighlight, setNewHighlight] = useState("");
    const [categoryInputs, setCategoryInputs] = useState({});

    const handleAddHighlight = (e) => {
        if (e.key === "Enter" && newHighlight.trim()) {
            const newSkills = newHighlight.split(";").map(s => s.trim()).filter(Boolean);
            setSkillsField("highlighted", [...skills.highlighted, ...newSkills]);
            setNewHighlight("");
        }
    };

    const removeHighlight = (index) => {
        setSkillsField("highlighted", skills.highlighted.filter((_, i) => i !== index));
    };

    const handleCategoryItemAdd = (catIndex, value) => {
        const val = value || categoryInputs[catIndex];
        if (val && val.trim()) {
            const newItems = val.split(/[;\n]/).map(i => i.trim()).filter(Boolean);
            const category = skills.categories[catIndex];
            updateSkillCategory(catIndex, {
                ...category,
                items: [...new Set([...category.items, ...newItems])]
            });
            setCategoryInputs(prev => ({ ...prev, [catIndex]: "" }));
        }
    };

    return (
        <div className="space-y-8" data-testid="skills-step">
            {/* Highlighted Skills */}
            <section className="space-y-4">
                <h3 className="text-lg font-semibold text-white border-b border-white/10 pb-2">Compétences Phares</h3>
                <div className="space-y-3">
                    <div className="flex gap-2">
                        <Input
                            data-cv-path="skills.highlighted" aria-label="Ajouter une compétence phare"
                            placeholder="Ajouter une compétence phare (séparer par ;)"
                            value={newHighlight}
                            onChange={(e) => setNewHighlight(e.target.value)}
                            onKeyDown={handleAddHighlight}
                            onBlur={() => {
                                if (newHighlight.trim()) {
                                    const newSkills = newHighlight.split(";").map(s => s.trim()).filter(Boolean);
                                    setSkillsField("highlighted", [...skills.highlighted, ...newSkills]);
                                    setNewHighlight("");
                                }
                            }}
                            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-lenvia-blue"
                        />
                        <Button
                            variant="secondary"
                            onClick={() => {
                                if (newHighlight.trim()) {
                                    setSkillsField("highlighted", [...skills.highlighted, newHighlight.trim()]);
                                    setNewHighlight("");
                                }
                            }}
                            className="bg-lenvia-blue hover:bg-lenvia-blue/80 text-white px-3"
                            title="Ajouter"
                        >
                            <Plus className="h-4 w-4" />
                        </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {skills.highlighted.map((skill, index) => (
                            <Badge key={index} data-cv-path={`skills.highlighted.${index}`} variant="secondary" className="px-3 py-1 text-sm bg-lenvia-blue/20 text-lenvia-blue border border-lenvia-blue/30">
                                {skill}
                                <X
                                    className="ml-2 h-3 w-3 cursor-pointer hover:text-destructive transition-colors"
                                    onClick={() => removeHighlight(index)}
                                />
                            </Badge>
                        ))}
                    </div>
                </div>
            </section>

            <section aria-label="Compétences affichées dans le CV" className="rounded-lg border border-blue-400/30 bg-blue-400/10 p-4 space-y-3">
                <h4 className="font-medium text-blue-100">Compétences affichées dans l’en-tête du CV</h4>
                <p className="text-sm text-slate-300">{skills.highlighted.length ? 'Votre sélection est prioritaire. Les catégories complètent les places disponibles, jusqu’à cinq compétences.' : 'Aucune sélection enregistrée : le CV utilise automatiquement ces compétences issues des catégories.'}</p>
                <p className="text-sm font-semibold text-white">{automaticSkills.join('  ●  ') || 'Aucune compétence disponible. Ajoutez-en dans les catégories ou dans votre sélection.'}</p>
                {automaticSkills.length > 0 && !skills.highlighted.length && <Button type="button" variant="outline" onClick={() => setSkillsField('highlighted', automaticSkills)}>Utiliser ces compétences comme sélection</Button>}
            </section>

            {/* Categories */}
            <section className="space-y-6">
                <div className="flex justify-between items-center border-b border-white/10 pb-2">
                    <h3 className="text-lg font-semibold text-white">Catégories de Compétences</h3>
                    <Button variant="outline" size="sm" onClick={addSkillCategory} className="border-white/10 text-slate-300 hover:bg-white/5 hover:text-white">
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter une catégorie
                    </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {skills.categories.map((category, catIndex) => (
                        <Card key={catIndex} className="bg-white/5 border-white/10 shadow-lg">
                            <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0 text-white">
                                <Input
                                    placeholder="Nom de la catégorie (ex: Frontend)"
                                    data-cv-path={`skills.categories.${catIndex}.name`} aria-label={`Catégorie de compétences ${catIndex + 1}`} value={category.name}
                                    onChange={(e) => updateSkillCategory(catIndex, { ...category, name: e.target.value })}
                                    className="h-8 font-bold border-none bg-transparent focus-visible:ring-0 px-0 text-white placeholder:text-slate-500"
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-slate-400 hover:text-red-400 hover:bg-red-400/10"
                                    onClick={() => removeSkillCategory(catIndex)}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="Éléments (ex: React; Vue) - séparer par ;"
                                            value={categoryInputs[catIndex] || ""}
                                            onChange={(e) => setCategoryInputs(prev => ({ ...prev, [catIndex]: e.target.value }))}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleCategoryItemAdd(catIndex);
                                                }
                                            }}
                                            onBlur={() => handleCategoryItemAdd(catIndex)}
                                            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 text-sm h-9"
                                        />
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handleCategoryItemAdd(catIndex)}
                                            className="bg-slate-700 hover:bg-slate-600 text-white h-9 px-3"
                                            title="Ajouter"
                                        >
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {category.items.map((item, itemIndex) => (
                                            <Badge key={itemIndex} data-cv-path={`skills.categories.${catIndex}.items.${itemIndex}`} variant="outline" className="text-xs bg-white/10 text-white border-white/20 px-2 py-0.5">
                                                {item}
                                                <X
                                                    className="ml-1.5 h-3 w-3 cursor-pointer text-slate-400 hover:text-red-400"
                                                    onClick={() => {
                                                        updateSkillCategory(catIndex, {
                                                            ...category,
                                                            items: category.items.filter((_, i) => i !== itemIndex)
                                                        });
                                                    }}
                                                />
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default SkillsStep;
